import { jwtDecode } from "jwt-decode";
import Cookies from "universal-cookie";

const PROPERTIES_WITHOUT_RIGHTS = ['id', 'icon', 'label', 'userBaseProjects', 'type', 'color', 'baseProject', 'baseProjectId', 'deletionDate', 'areas', 'projectRoleStations'];
const CATEGORIES_PROPERTIES = { treeCategories: 'trees', greenSpaceCategories: 'greenSpaces', furnitureCategories: 'furnitures' };

export default class RightsUtil {
    static getUserProjectRights(userProject) {
        return {
            ...this.getRights(),
            ...(userProject?.projectRole || {})
        };
    }

    static getRights(projectRole, readOnly = false) {
        let rights = {
            // Fonctionnalités
            filters: null,
            tables: null,
            statistics: null,
            charts: null,
            timeline: null,
            mapExport: null,
            actions: null,
            prices: null,
            projectHistory: null,
            thematicMaps: null,
            // Éléments
            trees: null,
            greenSpaces: null,
            furnitures: null,
            markers: null,
            stations: null,
            backgroundImages: null,
            // Gestion
            parameters: null,
            formulas: null,
            requiredFields: null,
            publicFields: null,
            collaborators: null,
            rights: null
        };

        if (projectRole) rights = JSON.parse(JSON.stringify(projectRole));
        if (readOnly) rights = this.setRightsReadOnly(rights);

        return rights;
    }

    static setRightsReadOnly(rights) {
        let newRights = { ...rights };
        newRights.filters = newRights.filters?.length ? newRights.filters.replace('W', '').replace('I', '') : null;
        newRights.tables = newRights.tables?.length ? newRights.tables.replace('W', '').replace('I', '') : null;
        newRights.statistics = newRights.statistics?.length ? newRights.statistics.replace('W', '').replace('I', '') : null;
        newRights.charts = newRights.charts?.length ? newRights.charts.replace('W', '').replace('I', '') : null;
        newRights.timeline = newRights.timeline?.length ? newRights.timeline.replace('W', '').replace('I', '') : null;
        newRights.actions = newRights.actions?.length ? newRights.actions.replace('W', '').replace('I', '') : null;
        newRights.thematicMaps = newRights.thematicMaps?.length ? newRights.thematicMaps.replace('W', '').replace('I', '') : null;
        newRights.trees = newRights.trees?.length ? newRights.trees.replace('W', '').replace('I', '') : null;
        newRights.greenSpaces = newRights.greenSpaces?.length ? newRights.greenSpaces.replace('W', '').replace('I', '') : null;
        newRights.furnitures = newRights.furnitures?.length ? newRights.furnitures.replace('W', '').replace('I', '') : null;
        newRights.markers = newRights.markers?.length ? newRights.markers.replace('W', '').replace('I', '') : null;
        newRights.stations = newRights.stations?.length ? newRights.stations.replace('W', '').replace('I', '') : null;
        newRights.backgroundImages = newRights.backgroundImages?.length ? newRights.backgroundImages.replace('W', '').replace('I', '') : null;
        return newRights;
    }

    static isReadOnly(rights) {
        return Object.keys(rights)
            .filter(key => !['id', 'icon', 'label', 'userBaseProjects', 'type', 'color', 'baseProjectId', 'deletionDate', 'areas', 'projectRoleStations'].includes(key))
            .find(key => (rights[key] || '').toLowerCase().includes('w')) ? false : true;
    }

    static canRead(right, detailedRight = null) { // Si detailedRight !== null, right = Nom du droit à checker, sinon right = Valeur du droit
        return detailedRight
            ? (!Object.keys(detailedRight).length || (detailedRight[right]?.includes('R')))
            : right?.includes('R');
    }

    static canWrite(right, detailedRight = null) {
        return detailedRight
            ? (!Object.keys(detailedRight).length || (detailedRight[right]?.includes('W')))
            : right?.includes('W');
    }

    static canImport(right) {
        return right?.includes('I');
    }

    static canImportGlobally(rights) {
        return this.canImport(rights.trees) || this.canImport(rights.greenSpaces) || this.canImport(rights.furnitures) || this.canImport(rights.stations);
    }

    static canExport(right) {
        return right?.includes('E');
    }

    static canExportGlobally(rights) {
        return this.canExport(rights.trees) || this.canExport(rights.greenSpaces) || this.canExport(rights.furnitures) || this.canExport(rights.stations);
    }

    static getUserProjectRole(userProjects) {
        const token = new Cookies().get('token');
        if (!token) return null;
        const userId = jwtDecode(token).id;
        return userProjects?.find(up => up.userId === userId)?.projectRole || null;
    }

    static getProjectRolesWithMembers(projectRoles, userProjects, baseProjectId) { // L'API ne renvoie pas les membres avec les rôles : on les reprend des collaborateurs du projet
        return projectRoles.map(projectRole => {
            const members = (userProjects || []).filter(up => up.projectRoleId === projectRole.id && up.baseProjectId === baseProjectId);
            return {
                ...projectRole,
                userBaseProjects: members
                    .filter((member, index) => !members.slice(index + 1).some(m => m.userId === member.userId)) // userProjects peut contenir des doublons
                    .map(({ projectRole, ...member }) => member)
            };
        });
    }

    static canEditProjectRole(userProjectRole, projectRole) { // Doit rester aligné avec RightsService.CanEditProjectRole côté API
        if (!userProjectRole?.id || !projectRole) return false; // Sans rôle identifié on ne peut rien modifier
        if (projectRole.type === 'owner') return false; // Le rôle propriétaire n'est jamais modifiable
        if (projectRole.type === 'manager') return userProjectRole.type === 'owner'; // Le rôle responsable n'est modifiable que par le propriétaire
        return userProjectRole.id !== projectRole.id; // On ne peut pas modifier son propre rôle
    }

    static canGrantRight(userRights, initialRights, right) { // On ne peut pas attribuer un droit que l'on ne possède pas soi-même
        return (initialRights || '').includes(right) || (userRights || '').includes(right);
    }

    // L'éditeur peut être restreint sur une catégorie sans l'être sur le droit global (ex : Arbres RW mais Emplacement en R).
    static canGrantElementRight(userProjectRole, initialProjectRole, elementProperty, right) {
        const categoriesProperty = Object.keys(CATEGORIES_PROPERTIES).find(key => CATEGORIES_PROPERTIES[key] === elementProperty);
        if (!categoriesProperty || !['R', 'W'].includes(right)) return true;
        return Object.keys(userProjectRole[categoriesProperty] || {}).every(category => this.canGrantRight(
            this.getCategoryRights(userProjectRole, categoriesProperty, category),
            this.getCategoryRights(initialProjectRole, categoriesProperty, category),
            right
        ));
    }

    static getCategoryRights(projectRole, categoriesProperty, category) {
        if (!projectRole) return null;
        const categories = projectRole[categoriesProperty];
        if (categories && category in categories) return categories[category];
        // Tant que le détail n'est pas renseigné, les catégories héritent du droit global de l'élément
        return (projectRole[CATEGORIES_PROPERTIES[categoriesProperty]] || '').replace(/[^RW]/g, '') || null;
    }

    static clampRights(rights, userRights, initialRights) { // Retire les droits que l'éditeur ne possède pas lui-même
        return (rights || '').split('').filter(right => (
            (initialRights || '').includes(right) || (userRights || '').includes(right)
        )).join('') || null;
    }

    static clampProjectRole(projectRole, userProjectRole, initialProjectRole) { // Ramène un rôle au maximum de ce que l'éditeur peut attribuer
        if (!userProjectRole || userProjectRole.type === 'owner') return projectRole;

        const clampedProjectRole = { ...projectRole };
        Object.keys(clampedProjectRole).forEach(property => {
            if (PROPERTIES_WITHOUT_RIGHTS.includes(property)) return;
            if (CATEGORIES_PROPERTIES[property]) {
                const categories = clampedProjectRole[property];
                if (!categories) return;
                clampedProjectRole[property] = Object.keys(categories).reduce((newCategories, category) => ({
                    ...newCategories,
                    [category]: this.clampRights(
                        categories[category],
                        this.getCategoryRights(userProjectRole, property, category),
                        this.getCategoryRights(initialProjectRole, property, category)
                    )
                }), {});
            } else if (typeof clampedProjectRole[property] === 'string' || clampedProjectRole[property] === null)
                clampedProjectRole[property] = this.clampRights(clampedProjectRole[property], userProjectRole[property], initialProjectRole?.[property]);
        });

        return clampedProjectRole;
    }

    static isOwner(rights) {
        return rights.type === 'owner';
    }

    static isOwnerOrManager(rights) {
        return ['owner', 'manager'].includes(rights.type);
    }

    static isRestricted(subscription, projectRole) {
        if (!subscription || !projectRole) return true;
        if (projectRole.type === 'owner') return false;
        projectRole = { ...projectRole, label: null, userBaseProjects: null, color: null, areas: null, type: null, icon: null };
        return (Object.values(projectRole).some(v => typeof v === 'string' && /[wiWI]/.test(v)) && subscription.shortName.toLowerCase() === 'free');
    }

    static checkIfUserHasRight(rights, project, projectCollaborators, layer) {
        const token = new Cookies().get('token');
        if (!token) return false;
        const userId = jwtDecode(token).id;
        if (!userId) return false;
        if (!project) return true;
        const userBaseProject = projectCollaborators?.find(x => x.userId === userId);
        if (!userBaseProject) return false;
        if (!layer || !Array.isArray(layer)) return false;
        const { category } = layer?.[0]?.feature?.properties;
        const right = category === 'Arbre' ? 'trees' : category === 'Espace vert' ? 'greenSpaces' : category === 'Mobilier' ? 'furnitures' : 'markers';
        return RightsUtil.canWrite(rights[right]);
    }
}